import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import PriceToStr from '../../_components/PriceToStr';
import { getCurrencyContrData } from '../../_helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontSize: '18px',
  },
  itemsList: {
    marginBottom: theme.spacing(2),
    maxHeight: '400px',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
  },
  item: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  itemName: {
    fontSize: '13px',
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
  },
  itemMeta: {
    fontSize: '12px',
    color: '#666',
  },
  itemPrice: {
    fontWeight: 600,
    fontSize: '14px',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(0.5),
  },
  summary: {
    marginTop: theme.spacing(2),
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    fontSize: '14px',
    '& p:last-child': {
        fontWeight: 400,
        color: '#8b8b8b',
    }
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '2px solid #ddd',
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.primary.main,
  },
  buttons: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  submitButton: {
    width: '100%',
    padding: theme.spacing(1.5),
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
  },
  backButton: {
    width: '100%',
    textTransform: 'none',
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#999',
    padding: theme.spacing(2),
  },
}));

/**
 * CheckoutSummary - показує кошик справа
 * Список товарів, вартість, кнопки
 */
const CheckoutSummary = ({ cartItems, paymentMethod, onSubmit, isLoading, userSelectedData, contragentData, frontendData }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  
  // Отримуємо дані про валюту для правильного витягування ціни
  const currencyMain =
    userSelectedData?.sell_currency_id &&
    frontendData?.currencies &&
    frontendData.currencies.find(
      (x) => x.id === userSelectedData.sell_currency_id
    );

  const currencyData = getCurrencyContrData(
    currencyMain,
    frontendData?.main_currency,
    userSelectedData?.sell_currency_id,
    contragentData?.customer_data,
    frontendData?.currencies
  );

  // Розрахунок загальної вартості
  const getItemPrice = (item) => {
    // Спочатку пробуємо витягти ціну з price_in_currencies (як у CartPage)
    if (item.price_in_currencies) {
      const currencyPrice =
        (currencyData?.abbr && item.price_in_currencies[currencyData.abbr]) ||
        Object.values(item.price_in_currencies)[0];

      if (currencyPrice) {
        const price =
          currencyPrice.price_discount ||
          currencyPrice.price ||
          currencyPrice.regular_price;
        if (price !== undefined && price !== null) {
          return parseFloat(price) || 0;
        }
      }
    }

    // Fallback до старої логіки, якщо price_in_currencies не доступна
    const price =
      item.price ||
      item.price_current ||
      item.price_sell ||
      item.unit_price ||
      item.price_total ||
      0;
    return parseFloat(price) || 0;
  };

  const getItemQuantity = (item) => {
    return (
      parseInt(item.quantity || item.count || item.amount || item.qty || 0, 10) || 0
    );
  };

  const totalPrice = cartItems?.reduce((sum, item) => {
    return sum + getItemPrice(item) * getItemQuantity(item);
  }, 0) || 0;

  const itemCount = cartItems?.reduce((sum, item) => sum + getItemQuantity(item), 0) || 0;

  const currencySymbol =
    currencyData?.simbol ||
    (currencyData?.abbr === 'UAH' ? '₴' : currencyData?.abbr) ||
    '₴';
  const currencyAbbr = currencyData?.abbr || 'UAH';
  const isUah = currencySymbol === '₴';

  const cartDimensions = cartItems?.reduce(
    (acc, item) => {
      const quantity = getItemQuantity(item);
      const weight = parseFloat(item.weight || 0) || 0;
      const volume = parseFloat(item.volume || 0) || 0;
      const packingRatio = parseFloat(item.packing_ratio || 1) || 1;
      const itemVolume = volume + packingRatio * volume * (quantity - 1);

      acc.totalWeight += weight * quantity * 1.2;
      acc.totalVolume += itemVolume;
      if (item.massive === 1 || item.massive === true) {
        acc.hasMassive = true;
      }
      return acc;
    },
    { totalWeight: 0, totalVolume: 0, hasMassive: false }
  );

  const allWeight = Math.ceil(cartDimensions.totalWeight || 0);
  const volumetricWeight = Math.ceil((cartDimensions.totalVolume || 0) * 1.2 * 250);
  const packagingCost = 0;
  const postpaidFee = paymentMethod === 'postpaid' ? Math.round(totalPrice * 0.04) : 0;
  const subtotalWithPostpaid = totalPrice + packagingCost + postpaidFee;

  const dispatchDate = new Date();
  dispatchDate.setDate(dispatchDate.getDate() + 1);
  const formattedDispatchDate = `${dispatchDate.getDate().toString().padStart(2, '0')}.${(
    dispatchDate.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}.${dispatchDate.getFullYear()}`;

  const renderPrice = (value) => (
    <PriceToStr
      data={value}
      text={currencySymbol}
      grn={isUah ? currencyAbbr : ''}
    />
  );

  return (
    <Box className={classes.root}>
      {/* Заголовок */}
      <Typography className={classes.title}>
        🛒 {t('myCart') || 'Ваше замовлення'}
      </Typography>

      {/* Список товарів */}
      {cartItems && cartItems.length > 0 ? (
        <>
          <Box className={classes.itemsList}>
            <List disablePadding>
              {cartItems.map((item, index) => (
                <ListItem key={index} className={classes.item}>
                  <ListItemText
                    primary={item.name || item.title || 'Товар'}
                    primaryTypographyProps={{ component: 'div', className: classes.itemName }}
                    secondary={
                      <Box component="div">
                        <Typography component="div" className={classes.itemMeta}>
                          {t('quantity') || 'Кількість'}: {item.quantity || 1}
                        </Typography>
                        {item.article && (
                          <Typography component="div" className={classes.itemMeta}>
                            Art: {item.article}
                          </Typography>
                        )}
                        <Typography component="div" className={classes.itemPrice}>
                          {renderPrice(getItemPrice(item) * getItemQuantity(item))}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Резюме */}
          <Divider style={{ margin: '16px 0' }} />
          <Box className={classes.summary}>
            <Box className={classes.summaryRow}>
              <Typography>{itemCount} {t('item') || 'товари'}</Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography>{t('shipmentDate') || 'Дата відвантаження'}:</Typography>
              <Typography>{formattedDispatchDate}</Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography>{t('weightVolume') || 'Вага / Об’ємна вага'}:</Typography>
              <Typography>{allWeight} кг / {volumetricWeight} кг</Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography>{t('productsTotal') || 'Товарів на суму'}:</Typography>
              <Typography>{renderPrice(totalPrice)}</Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography>{t('costOfAdditionalPackaging') || 'Вартість додаткового пакування'}:</Typography>
              <Typography>{renderPrice(packagingCost)}</Typography>
            </Box>
            <Box className={classes.summaryRow}>
              <Typography>{t('deliveryAmount') || 'Сума доставки'}:</Typography>
              <Typography>{t('common:At_carrier_rates') || 'За тарифами перевізника'}</Typography>
            </Box>
            {paymentMethod === 'postpaid' && (
              <Box className={classes.summaryRow}>
                <Typography>{t('postpaidFee') || 'Післяоплата +4%'}:</Typography>
                <Typography>{renderPrice(postpaidFee)}</Typography>
              </Box>
            )}
            <Box className={classes.totalRow}>
              <Typography>{t('toPay') || 'До сплати'}:</Typography>
              <Typography>{renderPrice(subtotalWithPostpaid)}</Typography>
            </Box>
          </Box>
        </>
      ) : (
        <Typography className={classes.emptyMessage}>
          {t('cartIsEmpty') || 'Кошик пустий'}
        </Typography>
      )}

      {/* Кнопки */}
      <Box className={classes.buttons}>
        <Button
          variant="outlined"
          className={classes.backButton}
          href="/cart"
        >
          {t('backToCart') || '← Повернутися до кошика'}
        </Button>
      </Box>
    </Box>
  );
};

export default CheckoutSummary;
